import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import mockup from "../../assets/mockup.png";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.08); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const shieldPulse = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(134,239,172,0.3), 0 0 60px rgba(134,239,172,0.1); }
  50% { box-shadow: 0 0 60px rgba(134,239,172,0.6), 0 0 120px rgba(134,239,172,0.2); }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(400%); opacity: 0; }
`;

const orbit = keyframes`
  from { transform: rotate(0deg) translateX(80px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
`;

const orbitReverse = keyframes`
  from { transform: rotate(0deg) translateX(60px) rotate(0deg); }
  to { transform: rotate(-360deg) translateX(60px) rotate(360deg); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

// Outer wrapper — height is set by JS on desktop only
const Wrapper = styled.div`
  position: relative;
  background: transparent;
`;

// On mobile/tablet this is just a normal-flow container, not sticky
const StickyContainer = styled.div`
  background: linear-gradient(
    135deg,
    #0d0015 0%,
    #1a0035 30%,
    #2d0060 60%,
    #1a0040 100%
  );
  overflow: hidden;
  position: relative;

  /* Desktop: sticky + fixed height */
  @media (min-width: 1025px) {
    position: sticky;
    top: 0;
    height: 100vh;
  }
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  animation: ${pulse} ${({ dur }) => dur || "4s"} ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0s"};
  ${({ top, left, right, bottom, size, color }) => `
    top: ${top};
    left: ${left};
    right: ${right};
    bottom: ${bottom};
    width: ${size};
    height: ${size};
    background: ${color};
  `}

  @media (max-width: 1024px) {
    filter: blur(60px);
  }
`;

// Desktop: horizontal flex track. Mobile/tablet: normal block flow
const Track = styled.div`
  will-change: transform;

  @media (min-width: 1025px) {
    display: flex;
    flex-direction: row;
    width: fit-content;
    height: 100%;
  }

  @media (max-width: 1024px) {
    display: block;
    width: 100%;
  }
`;

// Desktop: full-viewport horizontal panel. Mobile/tablet: auto-height vertical section
const Panel = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1025px) {
    width: 100vw;
    height: 100vh;
    padding: clamp(1rem, 5vw, 4rem);
    flex-shrink: 0;
  }

  @media (max-width: 1024px) {
    width: 100%;
    min-height: 100svh;
    padding: clamp(3rem, 6vw, 5rem) clamp(1.25rem, 5vw, 3rem)
      clamp(3rem, 6vw, 5rem);
    align-items: center;
  }

  @media (max-width: 480px) {
    min-height: auto;
    padding: 3.5rem 1.25rem;
  }
`;

// ─── Panel 1 — Intro ──────────────────────────────────────────────────────────

const IntroInner = styled.div`
  text-align: center;
  max-width: 680px;
  width: 100%;
`;

const Eyebrow = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.65rem, 2.5vw, 0.75rem);
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c084fc;
  margin-bottom: clamp(1rem, 4vw, 1.5rem);
`;

const MainTitle = styled.h2`
  font-family: "Mendl", serif;
  font-size: clamp(2.2rem, 8vw, 5.5rem);
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  margin-bottom: clamp(1rem, 4vw, 1.5rem);

  em {
    font-style: italic;
    background: linear-gradient(90deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
  }
`;

const SubText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.9rem, 3vw, 1.125rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.6;
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: clamp(1rem, 4vh, 2.5rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  span {
    font-family: "Mendl", sans-serif;
    font-size: 0.7rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.35);
  }

  /* Show on all sizes, but change label on mobile */
  @media (max-width: 1024px) {
    span {
      display: none;
    }
  }
`;

const ChevronWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Chevron = styled.div`
  width: 18px;
  height: 18px;
  border-right: 1.5px solid rgba(255, 255, 255, 0.3);
  border-bottom: 1.5px solid rgba(255, 255, 255, 0.3);
  transform: rotate(45deg);
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: ${({ d }) => d};
`;

// ─── Panel 2 — Security ───────────────────────────────────────────────────────

const StepGrid = styled.div`
  display: grid;
  gap: clamp(2rem, 5vw, 5rem);
  align-items: center;
  max-width: 1100px;
  width: 100%;

  @media (min-width: 1025px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const StepNumber = styled.div`
  font-family: "Mendl", serif;
  font-size: clamp(3rem, 10vw, 7rem);
  font-weight: 700;
  line-height: 1;
  color: rgba(168, 85, 247, 0.12);
  margin-bottom: clamp(-1rem, -4vw, -1.5rem);
`;

const SecurityTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(134, 239, 172, 0.08);
  border: 1px solid rgba(134, 239, 172, 0.3);
  border-radius: 100px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1rem;

  span {
    font-family: "Mendl", sans-serif;
    font-size: clamp(0.65rem, 2.5vw, 0.7rem);
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #d686ef;
  }

  div {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #8a4ade;
    box-shadow: 0 0 8px #c04ade;
  }
`;

const SecurityTitle = styled.h3`
  font-family: "Mendl", serif;
  font-size: clamp(1.6rem, 5vw, 3rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: clamp(1rem, 3vw, 1.25rem);

  em {
    font-style: italic;
    color: #c586ef;
  }
`;

const SecurityDesc = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.7;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);

  @media (min-width: 1025px) {
    max-width: 420px;
  }
`;

const SecurityStatRow = styled.div`
  display: flex;
  gap: clamp(1rem, 4vw, 2rem);

  @media (max-width: 1024px) {
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (min-width: 1025px) {
    justify-content: flex-start;
  }
`;

const SecurityStat = styled.div`
  text-align: center;

  div:first-child {
    font-family: "Mendl", serif;
    font-size: clamp(1.5rem, 5vw, 2rem);
    font-weight: 700;
    color: #9b4ade;
    margin-bottom: 0.2rem;
  }
  div:last-child {
    font-family: "Mendl", sans-serif;
    font-size: clamp(0.65rem, 2vw, 0.75rem);
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const ShieldVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: clamp(280px, 45vw, 420px);
  margin: 0 auto;
  width: 100%;

  @media (max-width: 480px) {
    height: 260px;
  }
`;

const ShieldCore = styled.div`
  width: clamp(90px, 18vw, 140px);
  height: clamp(90px, 18vw, 140px);
  border-radius: 50%;
  background: linear-gradient(135deg, #1e052e, #461453, #551665);
  border: 2px solid rgba(163, 74, 222, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.8rem, 5vw, 3.5rem);
  position: relative;
  z-index: 2;
  animation: ${shieldPulse} 3s ease-in-out infinite;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(180, 74, 222, 0.8),
      transparent
    );
    animation: ${scanLine} 2.5s ease-in-out infinite;
  }
`;

const OrbitDot = styled.div`
  position: absolute;
  width: ${({ s }) => s || "10px"};
  height: ${({ s }) => s || "10px"};
  border-radius: 50%;
  background: ${({ c }) => c || "#af4ade"};
  box-shadow: 0 0 14px ${({ c }) => c || "#9b4ade"};
  animation: ${({ rev }) => (rev ? orbitReverse : orbit)}
    ${({ dur }) => dur || "4s"} linear infinite;
  animation-delay: ${({ del }) => del || "0s"};

  /* Keep on tablet, hide only on small mobile */
  @media (max-width: 480px) {
    display: none;
  }
`;

const SecurityRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px solid ${({ c }) => c || "rgba(138, 74, 222, 0.2)"};
  ${({ s }) => `width: ${s}; height: ${s};`}

  @media (max-width: 480px) {
    display: none;
  }
`;

const SecurityBadge = styled.div`
  position: absolute;
  background: rgba(36, 5, 46, 0.9);
  border: 1px solid rgba(188, 74, 222, 0.4);
  border-radius: 12px;
  padding: 0.35rem 0.55rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  white-space: nowrap;

  span:first-child {
    font-size: clamp(0.65rem, 2vw, 0.85rem);
  }
  span:last-child {
    font-family: "Mendl", sans-serif;
    font-size: clamp(0.5rem, 1.6vw, 0.65rem);
    color: #c086ef;
  }

  @media (max-width: 480px) {
    /* Collapse to just the emoji on very small screens */
    span:last-child {
      display: none;
    }
    padding: 0.3rem;
  }
`;

// ─── Panel 3 — Rizz Up ────────────────────────────────────────────────────────

const LinkGrid = styled.div`
  display: grid;
  gap: clamp(2rem, 5vw, 5rem);
  align-items: center;
  max-width: 1300px;
  width: 100%;

  @media (min-width: 1025px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const LinkContent = styled.div`
  /* Mobile/tablet: visible by default, GSAP will animate it on desktop */
  @media (max-width: 1024px) {
    opacity: 1 !important;
    transform: none !important;
  }
`;

const LinkTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(251, 191, 36, 0.08);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 100px;
  padding: 0.3rem 0.9rem;
  margin-bottom: 1rem;

  span {
    font-family: "Mendl", sans-serif;
    font-size: clamp(0.65rem, 2.5vw, 0.7rem);
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #995ef7;
  }

  div {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9954e7;
    box-shadow: 0 0 8px #7624fb;
  }
`;

const LinkTitle = styled.h3`
  font-family: "Mendl", serif;
  font-size: clamp(1.6rem, 5vw, 3rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: clamp(1rem, 3vw, 1.25rem);

  em {
    font-style: italic;
    color: #9324fb;
  }
`;

const LinkDesc = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.7;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);

  @media (min-width: 1025px) {
    max-width: 420px;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  @media (max-width: 1024px) {
    align-items: center;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-align: left;

  @media (max-width: 1024px) {
    justify-content: center;
    text-align: center;
  }

  span:first-child {
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  span:last-child {
    font-family: "Mendl", sans-serif;
    font-size: clamp(0.8rem, 2.5vw, 0.9rem);
    font-weight: 300;
    color: rgba(255, 255, 255, 0.6);
  }
`;

const PhonePlaceholder = styled.div`
  width: clamp(650px, 55vw, 420px);
  height: 1200px;
  border-radius: clamp(28px, 5vw, 40px);
  /* background: linear-gradient(160deg, #1a0035, #2d0060); */
  /* border: 2px solid rgba(168, 85, 247, 0.4); */
  position: relative;
  overflow: hidden;
  box-shadow:
    0 40px 100px rgba(0, 0, 0, 0.6),
    0 0 60px rgba(168, 85, 247, 0.2);
  animation: ${floatY} 5s ease-in-out infinite;
  align-self: center;
  justify-self: center;
  margin: 0 auto;

  &::before {
    content: "";
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    width: clamp(44px, 10vw, 64px);
    height: 6px;
    /* background: rgba(255, 255, 255, 0.12); */
    border-radius: 3px;
  }

  /* Mobile/tablet: visible by default, GSAP handles desktop only */
  @media (max-width: 1024px) {
    opacity: 1 !important;
    transform: none !important;
  }
  @media (max-width: 650px) {
    width: clamp(350px, 35vw, 320px);
    height: 500px;
  }
`;

const PhoneScreen = styled.div`
  position: absolute;
  inset: 0;
  /* background: linear-gradient(
    180deg,
    rgba(168, 85, 247, 0.05) 0%,
    transparent 50%,
    rgba(168, 85, 247, 0.08) 100%
  ); */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.5rem;
  img {
    object-fit: contain;
    max-width: 700px;
    max-height: 70vh;
  }
  @media (max-width: 650px) {
    width: 300px;
    img {
      margin-left: 10%;
      width: 300px;
    }
  }
`;

const PhoneImageLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.55rem, 2vw, 0.65rem);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.4rem 0.7rem;
`;

const PhoneImageIcon = styled.div`
  font-size: clamp(1.5rem, 5vw, 2rem);
  opacity: 0.3;
`;

// ─── Divider between vertical panels on mobile/tablet ────────────────────────

const PanelDivider = styled.div`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.3),
      transparent
    );
    margin: 0 clamp(1.25rem, 5vw, 3rem);
  }
`;

// ─── SecurityVisual ───────────────────────────────────────────────────────────

function SecurityVisual() {
  return (
    <ShieldVisual>
      <SecurityRing s="clamp(200px, 38vw, 340px)" c="rgba(74,222,128,0.06)" />
      <SecurityRing s="clamp(150px, 28vw, 250px)" c="rgba(74,222,128,0.1)" />
      <SecurityRing s="clamp(100px, 18vw, 160px)" c="rgba(74,222,128,0.16)" />
      <ShieldCore>🛡️</ShieldCore>
      <OrbitDot s="10px" c="#a34ade" dur="5s" del="0s" />
      <OrbitDot s="8px" c="#c586ef" dur="7s" del="-2s" rev />
      <OrbitDot s="6px" c="#4135e6" dur="4s" del="-1s" />
      <SecurityBadge top="10%" left="5%">
        <span>🔒</span>
        <span>E2E encrypted</span>
      </SecurityBadge>
      <SecurityBadge top="18%" right="5%">
        <span>✅</span>
        <span>ID verified</span>
      </SecurityBadge>
      <SecurityBadge bottom="18%" left="5%">
        <span>👩‍💻</span>
        <span>Women-led</span>
      </SecurityBadge>
      <SecurityBadge bottom="10%" right="5%">
        <span>🚨</span>
        <span>24/7 support</span>
      </SecurityBadge>
    </ShieldVisual>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function HowAttractionWorks() {
  const wrapperRef = useRef(null);
  const trackRef = useRef(null);
  const introRef = useRef(null);

  const securityContentRef = useRef(null);
  const securityVisualRef = useRef(null);
  const linkContentRef = useRef(null);
  const phonePlaceholderRef = useRef(null);

  // Refs for mobile scroll-triggered fade-ins
  const panel2Ref = useRef(null);
  const panel3Ref = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    const isDesktop = window.innerWidth > 1024;

    // ── Mobile / Tablet: vertical scroll with fade-ins ──────────────────────
    if (!isDesktop) {
      // Ensure GSAP-set inline styles don't carry over from any prior run
      gsap.set(
        [
          securityContentRef.current,
          securityVisualRef.current,
          linkContentRef.current,
          phonePlaceholderRef.current,
        ],
        { clearProps: "all" },
      );

      // Fade-in panel 2 contents
      const panel2Els = [securityVisualRef.current, securityContentRef.current];
      gsap.from(panel2Els, {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.15,
        scrollTrigger: {
          trigger: panel2Ref.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });

      // Fade-in panel 3 contents
      const panel3Els = [linkContentRef.current, phonePlaceholderRef.current];
      gsap.from(panel3Els, {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: panel3Ref.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });

      return () => ScrollTrigger.getAll().forEach((t) => t.kill());
    }

    // ── Desktop: horizontal scroll ──────────────────────────────────────────
    const totalPanels = 3;
    const scrollDistance = (totalPanels - 1) * window.innerWidth;
    wrapper.style.height = `${scrollDistance + window.innerHeight}px`;

    const hScroll = gsap.to(track, {
      x: () => -scrollDistance,
      ease: "none",
      scrollTrigger: {
        trigger: wrapper,
        start: "top top",
        end: () => `+=${scrollDistance}`,
        scrub: 1,
        pin: ".sticky-container",
        anticipatePin: 1,
      },
    });

    const vw = window.innerWidth;

    // Intro fade out
    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: `+=${vw * 0.7}`,
          end: `+=${vw * 0.95}`,
          scrub: 0.6,
        },
      })
      .to(introRef.current, { opacity: 0, y: -50, ease: "power2.in" });

    // Panel 2 — security
    const secOffset = 1 * vw;
    gsap.set(securityContentRef.current, { opacity: 0, x: -80 });
    gsap.set(securityVisualRef.current, { opacity: 0, scale: 0.88, y: 30 });

    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: () => `+=${secOffset - vw * 0.65}`,
          end: () => `+=${secOffset + vw * 0.1}`,
          scrub: 0.8,
        },
      })
      .to(
        securityContentRef.current,
        { opacity: 1, x: 0, ease: "power2.out" },
        0,
      )
      .to(
        securityVisualRef.current,
        { opacity: 1, scale: 1, y: 0, ease: "power2.out" },
        0,
      );

    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: () => `+=${secOffset + vw * 0.55}`,
          end: () => `+=${secOffset + vw * 0.88}`,
          scrub: 0.8,
        },
      })
      .to(
        securityContentRef.current,
        { opacity: 0, x: 48, ease: "power2.in" },
        0,
      )
      .to(
        securityVisualRef.current,
        { opacity: 0, scale: 0.92, y: -20, ease: "power2.in" },
        0,
      );

    // Panel 3 — rizz up
    const linkOffset = 2 * vw;
    gsap.set(linkContentRef.current, { opacity: 0, x: -80 });
    gsap.set(phonePlaceholderRef.current, { opacity: 0, x: 60, scale: 0.92 });

    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: () => `+=${linkOffset - vw * 0.65}`,
          end: () => `+=${linkOffset + vw * 0.1}`,
          scrub: 0.8,
        },
      })
      .to(linkContentRef.current, { opacity: 1, x: 0, ease: "power2.out" }, 0)
      .to(
        phonePlaceholderRef.current,
        { opacity: 1, x: 0, scale: 1, ease: "power2.out" },
        0,
      );

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
      if (hScroll) hScroll.kill();
    };
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper ref={wrapperRef}>
        <StickyContainer className="sticky-container">
          <Orb
            top="10%"
            left="-10%"
            size="clamp(200px, 40vw, 400px)"
            color="rgba(124,58,237,0.25)"
            dur="5s"
            delay="0s"
          />
          <Orb
            top="50%"
            right="-8%"
            size="clamp(160px, 35vw, 350px)"
            color="rgba(232,121,249,0.15)"
            dur="6s"
            delay="1s"
          />
          <Orb
            bottom="-10%"
            left="30%"
            size="clamp(150px, 30vw, 300px)"
            color="rgba(168,85,247,0.2)"
            dur="4.5s"
            delay="0.5s"
          />

          <Track ref={trackRef}>
            {/* ── Panel 1: Intro ── */}
            <Panel ref={introRef}>
              <IntroInner>
                <Eyebrow>No games. No filters.</Eyebrow>
                <MainTitle>
                  You know what
                  <br />
                  you <em>want.</em>
                </MainTitle>
                <SubText>
                  Stop pretending otherwise.
                  <br />
                  Here's how it actually goes down.
                </SubText>
              </IntroInner>
              <ScrollHint>
                <span>Scroll to explore</span>
                <ChevronWrap>
                  <Chevron d="0s" />
                  <Chevron d="0.2s" />
                </ChevronWrap>
              </ScrollHint>
            </Panel>

            <PanelDivider />

            {/* ── Panel 2: Security ── */}
            <Panel ref={panel2Ref}>
              <StepGrid>
                <div ref={securityVisualRef}>
                  <SecurityVisual />
                </div>
                <div
                  ref={securityContentRef}
                  style={{ opacity: 0, transform: "translateX(-60px)" }}
                >
                  <StepNumber style={{ color: "rgba(188, 74, 222, 0.1)" }}>
                    01
                  </StepNumber>
                  <SecurityTag>
                    <div />
                    <span>Built Safe</span>
                  </SecurityTag>
                  <SecurityTitle>
                    Your safety is
                    <br />
                    <em>our obsession.</em>
                  </SecurityTitle>
                  <SecurityDesc>
                    Built from the ground up by a women-led team who refused to
                    compromise. Every feature, every decision — made with your
                    safety as the non-negotiable starting point.
                  </SecurityDesc>
                  <SecurityStatRow>
                    <SecurityStat>
                      <div>100%</div>
                      <div>ID verified</div>
                    </SecurityStat>
                    <SecurityStat>
                      <div>24/7</div>
                      <div>Safety support</div>
                    </SecurityStat>
                    <SecurityStat>
                      <div>E2EE</div>
                      <div>Encrypted chats</div>
                    </SecurityStat>
                  </SecurityStatRow>
                </div>
              </StepGrid>
            </Panel>

            <PanelDivider />

            {/* ── Panel 3: Rizz Up ── */}
            <Panel ref={panel3Ref}>
              <LinkGrid>
                <LinkContent ref={linkContentRef}>
                  <StepNumber style={{ color: "rgba(251,191,36,0.1)" }}>
                    02
                  </StepNumber>
                  <LinkTag>
                    <div />
                    <span>Rizz Up</span>
                  </LinkTag>
                  <LinkTitle>
                    Connecting has never been
                    <br />
                    <em>this easy and safe!</em>
                  </LinkTitle>
                  <LinkDesc>
                    No personality bios to read. No personalities to decode.
                    Just attraction, instinct, and play. Tap, spark, match,
                    repeat.
                  </LinkDesc>
                  <FeatureList>
                    <FeatureItem>
                      <span>⚡</span>
                      <span>Tap to spark — show interest in a second</span>
                    </FeatureItem>
                    <FeatureItem>
                      <span>🎮</span>
                      <span>Attraction score — see who's into you</span>
                    </FeatureItem>
                    <FeatureItem>
                      <span>📍</span>
                      <span>Nearby players — real-time proximity</span>
                    </FeatureItem>
                    <FeatureItem>
                      <span>🎯</span>
                      <span>Vibe-based matching</span>
                    </FeatureItem>
                  </FeatureList>
                </LinkContent>
                <PhonePlaceholder ref={phonePlaceholderRef}>
                  <PhoneScreen>
                    <img src={mockup} alt="" />
                  </PhoneScreen>
                </PhonePlaceholder>
              </LinkGrid>
            </Panel>
          </Track>
        </StickyContainer>
      </Wrapper>
    </>
  );
}
